import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDatabase } from '../db/DatabaseProvider';
import { useBranches, useServiceCategories, useBaseCurrency } from '../db/hooks/useLookupData';
import { useHasPermission } from '../components/auth/RequirePermission';
import { useToast } from '../components/common/ToastProvider';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { now } from '../lib/uuid';
import {
  Plus,
  Search,
  MoreVertical,
  Clock,
  Package,
  Tag,
  ChevronDown,
  Settings2,
  Sparkles,
  X,
  Layers
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { CustomSelect } from '../components/common/CustomSelect';
import { EmptyState } from '../components/common/EmptyState';
import { ServiceFormDrawer } from '../components/catalog/ServiceFormDrawer';
import { PackageFormDrawer } from '../components/catalog/PackageFormDrawer';
import { CategoryManagerDrawer } from '../components/catalog/CategoryManagerDrawer';
import { CategoryFilterSelect } from '../components/catalog/CategoryFilterSelect';
import type { ServiceDocType, PackageDocType } from '../db/schemas';

export function Catalog() {
  const db = useDatabase();
  const hasPermission = useHasPermission();
  const canManageSettings = hasPermission('settings:manage');
  const toast = useToast();

  const { branches } = useBranches();
  const { serviceCategories } = useServiceCategories();
  const { currencySymbol } = useBaseCurrency();

  const [activeTab, setActiveTab] = useState<'services' | 'combos'>('services');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [filterSede, setFilterSede] = useState('all');

  const [services, setServices] = useState<ServiceDocType[]>([]);
  const [combos, setCombos] = useState<PackageDocType[]>([]);

  const [isServicioFormOpen, setIsServicioFormOpen] = useState(false);
  const [isComboFormOpen, setIsComboFormOpen] = useState(false);
  const [isCategoryManagerOpen, setIsCategoryManagerOpen] = useState(false);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);

  const [editingService, setEditingService] = useState<ServiceDocType | null>(null);
  const [editingCombo, setEditingCombo] = useState<PackageDocType | null>(null);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  const addMenuRef = useRef<HTMLDivElement>(null);
  const optionsMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target as Node)) {
        setIsAddMenuOpen(false);
      }
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target as Node)) {
        setIsOptionsMenuOpen(false);
      }
    };
    if (isAddMenuOpen || isOptionsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAddMenuOpen, isOptionsMenuOpen]);

  // Sync data from RxDB
  useEffect(() => {
    if (!db) return;
    const subServices = db.services.find({ selector: { is_deleted: false } }).$.subscribe((docs) => {
      setServices(docs.map((d) => d.toJSON() as any));
    });
    const subCombos = db.packages.find({ selector: { is_deleted: false } }).$.subscribe((docs) => {
      setCombos(docs.map((d) => d.toJSON() as any));
    });
    return () => {
      subServices.unsubscribe();
      subCombos.unsubscribe();
    };
  }, [db]);

  // Actions
  const openNewServicio = () => {
    setEditingService(null);
    setIsServicioFormOpen(true);
  };

  const openNewCombo = () => {
    setEditingCombo(null);
    setIsComboFormOpen(true);
  };

  const handleDeleteServicio = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!db || !canManageSettings) return;
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Servicio',
      message: '¿Estás seguro de que deseas eliminar este servicio del catálogo?',
      onConfirm: async () => {
        try {
          const doc = await db.services.findOne({ selector: { id } }).exec();
          if (doc) await doc.patch({ is_deleted: true, updated_at: now() });
          toast.success('Servicio eliminado correctamente');
        } catch (err: any) {
          toast.error('Error al eliminar servicio: ' + err.message);
        } finally {
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  const handleDeleteCombo = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!db || !canManageSettings) return;
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Combo/Paquete',
      message: '¿Estás seguro de que deseas eliminar este combo promocional?',
      onConfirm: async () => {
        try {
          const doc = await db.packages.findOne({ selector: { id } }).exec();
          if (doc) await doc.patch({ is_deleted: true, updated_at: now() });
          toast.success('Combo promocional eliminado correctamente');
        } catch (err: any) {
          toast.error('Error al eliminar combo: ' + err.message);
        } finally {
          setConfirmModal((prev) => ({ ...prev, isOpen: false }));
        }
      },
    });
  };

  // Derived filtered services
  const filteredServices = useMemo(() => {
    return services.filter((s) => {
      const term = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !term ||
        s.concept.toLowerCase().includes(term) ||
        (s.description && s.description.toLowerCase().includes(term));

      let matchesCategory = true;
      if (selectedCategoryIds.length > 0) {
        const hasUncategorizedSelected = selectedCategoryIds.includes('__uncategorized__');
        const hasSpecificCat = s.category_id && selectedCategoryIds.includes(s.category_id);
        const isUncategorized = !s.category_id && hasUncategorizedSelected;
        matchesCategory = Boolean(hasSpecificCat || isUncategorized);
      }

      const matchesSede = filterSede === 'all' || s.branch_id === filterSede || !s.branch_id;
      return matchesSearch && matchesCategory && matchesSede;
    });
  }, [services, searchQuery, selectedCategoryIds, filterSede]);

  // Derived filtered combos
  const filteredCombos = useMemo(() => {
    return combos.filter((c) => {
      const term = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !term ||
        c.name.toLowerCase().includes(term) ||
        (c.description && c.description.toLowerCase().includes(term));
      const matchesSede = filterSede === 'all' || c.branch_id === filterSede || !c.branch_id;
      return matchesSearch && matchesSede;
    });
  }, [combos, searchQuery, filterSede]);

  // Group filtered services by category
  const servicesByCategory = useMemo(() => {
    const grouped: Record<string, ServiceDocType[]> = {};
    filteredServices.forEach((s) => {
      const catKey = s.category_id || '__uncategorized__';
      if (!grouped[catKey]) grouped[catKey] = [];
      grouped[catKey].push(s);
    });
    return grouped;
  }, [filteredServices]);

  const isFilteringActive = searchQuery.trim() !== '' || selectedCategoryIds.length > 0 || filterSede !== 'all';

  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedCategoryIds([]);
    setFilterSede('all');
  };

  return (
    <div className="mx-auto max-w-7xl h-full flex flex-col space-y-5 animate-fade-in p-4 md:p-6 pb-24 md:pb-8">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            Catálogo de Servicios
          </h1>
          <p className="text-sm text-text-secondary mt-0.5">
            Gestiona tus servicios, precios y paquetes promocionales.
          </p>
        </div>

        {canManageSettings && (
          <div className="flex items-center gap-2 self-start md:self-auto">
            {/* Options Dropdown */}
            <div className="relative" ref={optionsMenuRef}>
              <Button
                variant="secondary"
                icon={ChevronDown}
                onClick={() => setIsOptionsMenuOpen((prev) => !prev)}
              >
                Opciones
              </Button>
              {isOptionsMenuOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-56 bg-surface border border-border rounded-2xl shadow-2xl z-50 py-1.5 animate-scale-in">
                  <button
                    type="button"
                    onClick={() => {
                      setIsOptionsMenuOpen(false);
                      setIsCategoryManagerOpen(true);
                    }}
                    className="w-full text-left px-4 py-2.5 text-xs font-semibold text-text-primary hover:bg-surface-secondary flex items-center gap-2.5 cursor-pointer transition-colors"
                  >
                    <Layers className="w-4 h-4 text-brand-orange" />
                    <span>Gestionar Categorías</span>
                  </button>
                </div>
              )}
            </div>

            {/* Add Menu Dropdown */}
            <div className="relative" ref={addMenuRef}>
              <Button
                variant="primary"
                icon={Plus}
                onClick={() => setIsAddMenuOpen((prev) => !prev)}
              >
                Añadir
              </Button>
              {isAddMenuOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-52 bg-surface border border-border rounded-2xl shadow-2xl z-50 py-1.5 animate-scale-in">
                  <button
                    type="button"
                    onClick={() => {
                      openNewServicio();
                      setIsAddMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-text-primary hover:bg-surface-secondary flex items-center gap-2.5 cursor-pointer transition-colors"
                  >
                    <Tag className="w-4 h-4 text-brand-orange" />
                    <span className="font-semibold">Nuevo Servicio</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      openNewCombo();
                      setIsAddMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-text-primary hover:bg-surface-secondary flex items-center gap-2.5 cursor-pointer transition-colors border-t border-border/40"
                  >
                    <Package className="w-4 h-4 text-brand-orange" />
                    <span className="font-semibold">Nuevo Combo / Paquete</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Segmented Main Tabs ── */}
      <div className="flex items-center gap-2 p-1 bg-surface-secondary/70 border border-border rounded-2xl w-full sm:w-fit shadow-xs">
        <button
          type="button"
          onClick={() => setActiveTab('services')}
          className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'services'
              ? 'bg-brand-orange text-white shadow-sm shadow-brand-orange/20'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
          }`}
        >
          <Tag className="w-3.5 h-3.5" />
          <span>Servicios</span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              activeTab === 'services' ? 'bg-white/20 text-white' : 'bg-surface text-text-tertiary'
            }`}
          >
            {services.length}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('combos')}
          className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'combos'
              ? 'bg-brand-orange text-white shadow-sm shadow-brand-orange/20'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover'
          }`}
        >
          <Package className="w-3.5 h-3.5" />
          <span>Combos y Paquetes</span>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full ${
              activeTab === 'combos' ? 'bg-white/20 text-white' : 'bg-surface text-text-tertiary'
            }`}
          >
            {combos.length}
          </span>
        </button>
      </div>

      {/* ── Filter Bar ── */}
      <div className="flex flex-col md:flex-row gap-3 bg-surface-secondary/50 p-3.5 rounded-2xl border border-border shadow-xs">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
          <input
            type="text"
            placeholder={activeTab === 'services' ? 'Buscar servicios por nombre...' : 'Buscar paquetes por nombre...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-10 pr-4 bg-surface border border-border rounded-xl text-xs text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20 transition-all placeholder-text-tertiary shadow-xs"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary p-1 cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Category Multi-select Filter (Only for services) */}
        {activeTab === 'services' && (
          <div className="w-full md:w-64">
            <CategoryFilterSelect
              selectedCategories={selectedCategoryIds}
              onChange={setSelectedCategoryIds}
              serviceCategories={serviceCategories}
              services={services}
              onOpenManager={() => setIsCategoryManagerOpen(true)}
            />
          </div>
        )}

        {/* Branch Filter */}
        <div className="w-full md:w-56">
          <CustomSelect
            value={filterSede}
            onChange={setFilterSede}
            options={[
              { value: 'all', label: 'Todas las Sedes' },
              ...branches.map((s) => ({ value: s.id, label: s.name })),
            ]}
          />
        </div>

        {isFilteringActive && (
          <Button
            variant="ghost"
            size="sm"
            icon={X}
            onClick={clearAllFilters}
            className="text-text-tertiary hover:text-brand-orange"
          >
            Limpiar
          </Button>
        )}
      </div>

      {/* ── Main Content Grid ── */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
        {activeTab === 'services' ? (
          <div>
            {filteredServices.length === 0 ? (
              <EmptyState
                type={isFilteringActive ? 'no-search' : 'empty'}
                icon={Sparkles}
                title={isFilteringActive ? 'No se encontraron servicios' : 'Tu catálogo está vacío'}
                description={
                  isFilteringActive
                    ? 'No hay servicios que coincidan con los filtros aplicados. Prueba ajustando la búsqueda o las categorías.'
                    : 'Añade tu primer servicio para comenzar a configurar tu catálogo y precios.'
                }
                searchQuery={searchQuery}
                onClearSearch={isFilteringActive ? clearAllFilters : undefined}
                actionButton={
                  canManageSettings ? (
                    <Button variant="primary" size="sm" icon={Plus} onClick={openNewServicio}>
                      Crear Servicio
                    </Button>
                  ) : null
                }
              />
            ) : (
              <div className="space-y-8">
                {Object.entries(servicesByCategory).map(([catKey, items]) => {
                  const isUncategorized = catKey === '__uncategorized__';
                  const categoryDoc = !isUncategorized ? serviceCategories.find((c) => c.id === catKey) : null;
                  const displayNombre = isUncategorized ? 'Sin categoría' : (categoryDoc ? categoryDoc.name : 'Sin categoría');
                  const displayColor = isUncategorized ? '#64748B' : (categoryDoc?.color || '#F97316');

                  return (
                    <div key={catKey} className="space-y-3.5">
                      <div className="flex items-center justify-between gap-3 border-b border-border/50 pb-2.5">
                        <div className="flex items-center gap-2.5">
                          <span
                            className="w-3 h-3 rounded-full shadow-xs shrink-0"
                            style={{ backgroundColor: displayColor }}
                          />
                          <h3 className={`text-base font-black tracking-tight ${isUncategorized ? 'text-text-secondary italic' : 'text-text-primary'}`}>
                            {displayNombre}
                          </h3>
                          <span className="text-[11px] font-bold text-text-tertiary bg-surface-secondary px-2 py-0.5 rounded-md">
                            {items.length}
                          </span>
                        </div>

                        {canManageSettings && categoryDoc && (
                          <button
                            type="button"
                            onClick={() => setIsCategoryManagerOpen(true)}
                            className="text-xs font-semibold text-text-tertiary hover:text-brand-orange flex items-center gap-1 transition-colors cursor-pointer"
                          >
                            <Settings2 className="w-3 h-3" />
                            <span className="hidden sm:inline">Gestionar</span>
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {items.map((s) => (
                          <div
                            key={s.id}
                            onClick={() => {
                              setEditingService(s);
                              setIsServicioFormOpen(true);
                            }}
                            className="bg-surface border-y border-r border-l-[4px] border-border rounded-2xl p-4 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col group justify-between"
                            style={{ borderLeftColor: displayColor }}
                          >
                            <div className="flex justify-between items-start gap-2 mb-2">
                              <h4 className="font-bold text-sm text-text-primary line-clamp-2 leading-tight">
                                {s.concept}
                              </h4>
                              {canManageSettings && (
                                <button
                                  type="button"
                                  onClick={(e) => handleDeleteServicio(s.id, e)}
                                  className="text-text-tertiary hover:text-rose-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity p-1 cursor-pointer"
                                  title="Eliminar servicio"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </button>
                              )}
                            </div>

                            {s.description && (
                              <p className="text-xs text-text-tertiary line-clamp-2 mb-3">
                                {s.description}
                              </p>
                            )}

                            <div className="mt-auto pt-3 flex items-center justify-between text-xs border-t border-border/40">
                              <div className="flex items-center gap-1 text-text-secondary">
                                <Clock className="h-3.5 w-3.5 text-text-tertiary" />
                                <span className="font-medium">{s.duration_minutes || 30} min</span>
                              </div>
                              <span className="font-black text-brand-orange text-sm">
                                {currencySymbol}{s.currency_price.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div>
            {filteredCombos.length === 0 ? (
              <EmptyState
                type={isFilteringActive ? 'no-search' : 'empty'}
                icon={Package}
                title={isFilteringActive ? 'No se encontraron combos' : 'No hay combos creados'}
                description={
                  isFilteringActive
                    ? 'No hay combos promocionales que coincidan con la búsqueda.'
                    : 'Crea tu primer paquete o combo promocional agrupando múltiples sesiones y servicios.'
                }
                searchQuery={searchQuery}
                onClearSearch={isFilteringActive ? clearAllFilters : undefined}
                actionButton={
                  canManageSettings ? (
                    <Button variant="primary" size="sm" icon={Plus} onClick={openNewCombo}>
                      Crear Combo / Paquete
                    </Button>
                  ) : null
                }
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredCombos.map((c) => {
                  const items = c.items || [];
                  const regPrice = items.reduce((acc, it) => {
                    const s = services.find((serv) => serv.id === it.service_id);
                    return acc + (s ? s.currency_price * it.session_count : 0);
                  }, 0);

                  return (
                    <div
                      key={c.id}
                      onClick={() => {
                        setEditingCombo(c);
                        setIsComboFormOpen(true);
                      }}
                      className="bg-surface border border-border rounded-2xl p-5 shadow-xs hover:shadow-md hover:border-brand-orange/40 hover:-translate-y-0.5 transition-all cursor-pointer flex flex-col group justify-between"
                    >
                      <div>
                        <div className="flex justify-between items-start gap-3 mb-3">
                          <div className="flex items-start gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-brand-orange/10 flex items-center justify-center border border-brand-orange/20 shrink-0">
                              <Package className="h-4 w-4 text-brand-orange" />
                            </div>
                            <div>
                              <h4 className="font-extrabold text-sm text-text-primary leading-tight">
                                {c.name}
                              </h4>
                              {c.description && (
                                <p className="text-xs text-text-secondary mt-1 line-clamp-2">
                                  {c.description}
                                </p>
                              )}
                            </div>
                          </div>
                          {canManageSettings && (
                            <button
                              type="button"
                              onClick={(e) => handleDeleteCombo(c.id, e)}
                              className="text-text-tertiary hover:text-rose-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity p-1 cursor-pointer"
                              title="Eliminar paquete"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        {/* Items preview */}
                        <div className="bg-surface-secondary/60 rounded-xl p-3 mb-4 border border-border/50">
                          <p className="text-[10px] font-bold uppercase text-brand-orange mb-2 tracking-wider flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-orange" />
                            Contenido del paquete
                          </p>
                          <div className="space-y-1.5">
                            {items.map((it, idx) => {
                              const s = services.find((srv) => srv.id === it.service_id);
                              return (
                                <div key={idx} className="flex justify-between items-center text-xs">
                                  <span className="text-text-secondary truncate pr-2">
                                    {s ? s.concept : 'Servicio eliminado'}
                                  </span>
                                  <span className="font-bold text-text-primary bg-surface px-1.5 py-0.5 rounded-md border border-border/60 shrink-0 text-[11px]">
                                    {it.session_count}x
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between border-t border-border pt-3 mt-auto">
                        <div className="text-xs text-text-tertiary line-through">
                          Reg: {currencySymbol}{regPrice.toFixed(2)}
                        </div>
                        <div className="font-black text-base text-brand-orange">
                          {currencySymbol}{c.currency_price.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Drawers & Dialogs ── */}
      <ServiceFormDrawer
        isOpen={isServicioFormOpen}
        onClose={() => setIsServicioFormOpen(false)}
        service={editingService}
      />

      <PackageFormDrawer
        isOpen={isComboFormOpen}
        onClose={() => setIsComboFormOpen(false)}
        combo={editingCombo}
        services={services}
      />

      <CategoryManagerDrawer
        isOpen={isCategoryManagerOpen}
        onClose={() => setIsCategoryManagerOpen(false)}
      />

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Eliminar"
      />
    </div>
  );
}
